import { Radio, InputNumber, Space } from 'antd';
import { useEffect, useState } from 'react';

const TAX_OPTS = [
  { value: '0.0', label: '0 %' },
  { value: '9.5', label: '9.5 %' },
  { value: '22.0', label: '22 %' },
  { value: 'OTHR', label: 'OTHR' },
];

const PRESET_OPTS = TAX_OPTS.filter(o => o.value !== 'OTHR');

const TaxRateSelect = ({ value, onChange, disabled = false }) => {
  // Match presets numerically so 22, '22.0' and '22.00' all map to the 22 % preset.
  const matchedPreset =
    value != null && value !== '' && value !== 'OTHR'
      ? PRESET_OPTS.find(o => Number(o.value) === Number(value))
      : undefined;
  const isCustomValue = value != null && value !== '' && value !== 'OTHR' && !matchedPreset;

  // Two separate reasons the custom box can be on screen, kept apart because only
  // one of them is allowed to take the caret: `customMode` is a click on OTHR,
  // `autoShown` is a derived rate (e.g. 18.66 % from a typed DDV) that happens to
  // match no preset. Only the former is user-initiated.
  const [customMode, setCustomMode] = useState(false);
  const [autoShown, setAutoShown] = useState(isCustomValue);

  // Latch the box open. Deriving visibility straight from the value unmounted it
  // mid-keystroke as soon as what you typed hit a preset (22), dropping focus to
  // <body>. It now closes only when the user picks a preset button; the drawer
  // remounts this component on reset/intent switch to clear the latch.
  useEffect(() => {
    if (isCustomValue) setAutoShown(true);
  }, [isCustomValue]);

  const showCustomInput = customMode || autoShown;
  const radioValue = showCustomInput ? 'OTHR' : matchedPreset?.value;

  const handleRadioChange = (e) => {
    if (e.target.value === 'OTHR') {
      setCustomMode(true);
      onChange?.(null);
    } else {
      setCustomMode(false);
      setAutoShown(false);
      onChange?.(e.target.value);
    }
  };

  const handleCustomInput = (val) => {
    onChange?.(val != null ? String(val) : null);
  };

  const visibleOpts = showCustomInput ? TAX_OPTS.filter(o => o.value !== 'OTHR') : TAX_OPTS;
  const hasValue = value != null && value !== '' && value !== 'OTHR';

  return (
    <Space>
      <Radio.Group
        disabled={disabled}
        value={radioValue}
        onChange={handleRadioChange}
        options={visibleOpts}
        optionType="button"
        buttonStyle="solid"
      />
      {showCustomInput && (
        // autoFocus only when the user opened this themselves. A box that mounts
        // because a calculation derived a non-preset rate must never pull the
        // caret out of the amount field the user is still typing in.
        <InputNumber
          autoFocus={customMode}
          disabled={disabled}
          value={hasValue ? value : null}
          onChange={handleCustomInput}
          min={0}
          max={100}
          precision={2}
          step={0.1}
          addonAfter="%"
          placeholder="5.5"
          style={{ width: 100, ...(hasValue && { boxShadow: '0 0 0 2px #1677ff', borderRadius: 6 }) }}
        />
      )}
    </Space>
  );
};

export default TaxRateSelect;
